import { useEffect, useState } from "react";

import { ExpressionNode } from "@/components/expression/ExpressionNode";
import { MathThemeContext } from "@/components/expression/theme";
import { hintLightTheme } from "@/components/expression/theme/corca-themes";
import { NodeStyleProvider } from "@/components/expression/utils/NodeStyleProvider";
import { truncateNode } from "@/components/tiptap/phantom-hint/truncateNode";
import { DocumentModel } from "@/models/DocumentModel";
import { nodeFromJson } from "@/models/nodes/nodeFromJson";
import { twMerge } from "@/utils/twMerge";

import type { NodeModel } from "@/models/nodes/types";
import type { NodeJson } from "@corca/data";

/** A scratch document holding the ghost expressions on screen. */
let hintDoc: DocumentModel | undefined;

function getHintDoc(): DocumentModel {
  hintDoc ??= new DocumentModel("phantomHintDoc", null);
  return hintDoc;
}

type Props = {
  node: NodeJson;
  className?: string;
};

/** The faded expression a phantom hint or a hover preview offers to insert. */
export function HintExpression({ node, className }: Props) {
  const [model, setModel] = useState<NodeModel | null>(null);

  useEffect(() => {
    const doc = getHintDoc();
    const created = nodeFromJson(doc, truncateNode(node), { resetIds: true });
    setModel(created);

    return () => {
      if (created) {
        doc.transact(() => doc.removeNode(created), "HintExpression:unmount");
      }
    };
  }, [node]);

  if (!model) {
    return null;
  }

  return (
    <MathThemeContext.Provider value={hintLightTheme}>
      <NodeStyleProvider renderContext="suggestion">
        <ExpressionNode
          node={model}
          className={twMerge("opacity-40 dark:opacity-50 font-mono", className)}
        />
      </NodeStyleProvider>
    </MathThemeContext.Provider>
  );
}
